import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";
import { API, logErr, fmtErr } from "../../lib/api";

export default function AdminInventory() {
  const [items, setItems] = useState(null);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(null);
  const [adj, setAdj] = useState({});
  const [tab, setTab] = useState("stock");

  const load = useCallback(() => API.get("/admin/inventory").then((r) => setItems(r.data)).catch((err) => logErr("Failed to load inventory:", err)), [API, logErr]);
  const loadLogs = useCallback(() => API.get("/admin/stock-logs").then((r) => setLogs(r.data)).catch((err) => logErr("Failed to load stock logs:", err)), [API, logErr]);
  useEffect(() => {
    document.title = "Inventory | Admin";
    load();
    loadLogs();
  }, [load, loadLogs]);

  const adjust = async (p, v) => {
    const key = `${p.id}|${v.size}|${v.color}`;
    const change = parseInt(adj[key] || "0");
    if (!change) return toast.error("Enter a non-zero change (+/-)");
    try {
      await API.post(`/admin/products/${p.id}/stock`, { size: v.size, color: v.color, change, note: "Manual adjustment" });
      toast.success(`Stock ${change > 0 ? "added" : "reduced"}`);
      setAdj({ ...adj, [key]: "" });
      load();
      loadLogs();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!items) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="admin-inventory">
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Inventory</h1>
      <div className="flex gap-2 mb-6 border-b border-[#262626]">
        {[["stock", "Stock levels"], ["logs", "Adjustment logs"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`inv-tab-${k}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-bold border-b-2 transition-colors ${tab === k ? "border-gold text-gold" : "border-transparent text-muted-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "stock" ? (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="border border-[#262626] bg-ink-surface" data-testid={`inv-${p.slug}`}>
              <button onClick={() => setOpen(open === p.id ? null : p.id)} className="w-full flex items-center justify-between p-4 text-left">
                <div>
                  <p className="font-bold text-sm">{p.name}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{p.category}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xs font-bold px-2 py-1 uppercase ${p.status === "out" ? "bg-red-400/15 text-red-400" : p.status === "low" ? "bg-gold/15 text-gold" : "bg-green-400/10 text-green-400"}`}
                    data-testid={`inv-status-${p.slug}`}>
                    {p.status === "out" ? "Out of stock" : p.status === "low" ? `Low · ${p.total_stock}` : `${p.total_stock} in stock`}
                  </span>
                  {open === p.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </button>
              {open === p.id && (
                <div className="border-t border-[#262626] p-4 overflow-x-auto">
                  <table className="w-full text-xs min-w-[480px]">
                    <thead><tr className="text-left text-muted-foreground uppercase tracking-widest text-[9px]">
                      <th className="pb-2">Size</th><th className="pb-2">Color</th><th className="pb-2">SKU</th><th className="pb-2">Stock</th><th className="pb-2">Adjust</th>
                    </tr></thead>
                    <tbody>
                      {p.variants.map((v) => {
                        const key = `${p.id}|${v.size}|${v.color}`;
                        return (
                          <tr key={key} className="border-t border-[#262626]">
                            <td className="py-2 font-bold">{v.size}</td>
                            <td className="py-2">{v.color}</td>
                            <td className="py-2 text-muted-foreground">{v.sku}</td>
                            <td className={`py-2 font-bold ${v.stock === 0 ? "text-red-400" : v.stock <= 5 ? "text-gold" : ""}`}>{v.stock}</td>
                            <td className="py-2">
                              <div className="flex gap-2">
                                <input type="number" value={adj[key] || ""} onChange={(e) => setAdj({ ...adj, [key]: e.target.value })}
                                  placeholder="+5 / -2" data-testid={`adj-input-${p.slug}-${v.size}-${v.color}`}
                                  className="w-20 bg-ink border border-[#262626] px-2 py-1 outline-none focus:border-gold" />
                                <button onClick={() => adjust(p, v)} data-testid={`adj-btn-${p.slug}-${v.size}-${v.color}`}
                                  className="border border-gold text-gold px-3 py-1 font-bold hover:bg-gold/10 transition-colors">Apply</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-[#262626] bg-ink-surface" data-testid="stock-logs">
          {logs.length === 0 && <p className="p-6 text-xs text-muted-foreground">No adjustments logged yet.</p>}
          {logs.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-[#262626] text-xs">
              <div>
                <p className="font-bold">{l.product_name} <span className="text-muted-foreground font-normal">({l.size}/{l.color})</span></p>
                <p className="text-muted-foreground">{l.note} · by {l.by}</p>
              </div>
              <div className="text-right">
                <p className={`font-bold ${l.change > 0 ? "text-green-400" : "text-red-400"}`}>{l.change > 0 ? "+" : ""}{l.change}</p>
                <p className="text-muted-foreground">{l.at.slice(0, 16).replace("T", " ")}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
